import type { MediaFileDto } from '../../data/dto/media-file.dto.ts'
import type { MediaFolderDto } from '../../data/dto/media-folder.dto.ts'
import { type MediaFile, MediaFileCategory } from '../models/media-file.ts'
import type { FolderContent, MediaFolder } from '../models/media-folder.ts'
import type { FolderContentDto } from '../../data/dto/folder-content.dto.ts'

export const mapMediaFolderDtoToMediaFolder = (
  folder: MediaFolderDto,
  path: string
): MediaFolder => ({
  id: folder.folderId,
  path: path ? `${path}/${folder.folderName}` : `/${folder.folderName}`,
  name: folder.folderName,
  createdAt: folder.createdAt,
  createdBy: folder.createdBy,
  lastModifiedAt: folder.lastModified,
  parentFolderId: folder.parentFolderId,
})

export const mapMediaFileDtoToMediaFile = ({
  fileId,
  fileName,
  createdBy,
  createdAt,
  lastModified,
  duration,
  fileCategory,
  fileType,
  size,
  status,
  url,
}: MediaFileDto): MediaFile => {
  const { fileCategoryId, name: categoryName } = fileCategory ?? {}
  const { fileTypeId, name: typeName } = fileType ?? {}

  const safeCategoryName =
    categoryName && Object.values(MediaFileCategory).includes(categoryName)
      ? categoryName
      : undefined

  return {
    id: fileId,
    name: fileName,
    createdBy,
    createdAt,
    updatedAt: lastModified,
    duration,
    category: { id: fileCategoryId, name: safeCategoryName },
    type: { id: fileTypeId, name: typeName },
    size,
    status,
    url,
  }
}

export const mapFolderContentDtoToFolderContent = (
  content: FolderContentDto
): FolderContent => {
  const pathChain = content.currentPathChain
  if (!pathChain[0]) {
    throw new Error('Path chain is empty')
  }

  const pathChainToString = (pathChain: MediaFolderDto[]): string =>
    [...pathChain]
      .reverse()
      .map(({ parentFolderId, folderName }) =>
        parentFolderId ? folderName : ''
      )
      .join('/')

  const folders = content.folders.map((folder) =>
    mapMediaFolderDtoToMediaFolder(folder, pathChainToString(pathChain))
  )
  const files = content.files.map((file) => mapMediaFileDtoToMediaFile(file))
  const isRoot = pathChain.length === 1
  const folderId = pathChain.at(-1)?.folderId

  return {
    folderId,
    pathChain: pathChain.map((folder) =>
      mapMediaFolderDtoToMediaFolder(folder, pathChainToString(pathChain))
    ),
    isRoot,
    currentFolder: mapMediaFolderDtoToMediaFolder(
      pathChain[0],
      pathChainToString(pathChain.slice(0, -1))
    ),
    parentFolder:
      !isRoot && pathChain[1]
        ? mapMediaFolderDtoToMediaFolder(
            pathChain[1],
            pathChainToString(pathChain.slice(0, -2))
          )
        : undefined,
    folders,
    files,
  }
}
